import {
  ArrowLeft,
  CalendarDays,
  MessageSquareText,
  RefreshCcw,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { supabase } from "../lib/supabase";

type ReservationRow = {
  id: string;
  guest_name: string;
  email: string;
  reservation_date: string;
  reservation_time: string;
  guest_label: string;
  note: string | null;
  status: string;
  created_at: string;
};

type EnquiryRow = {
  id: string;
  guest_name: string;
  phone: string;
  email: string | null;
  service_type: string;
  event_date: string | null;
  guest_count: number | null;
  budget: string | null;
  location: string | null;
  preferred_time: string | null;
  requirements: string | null;
  dietary_requirements: string | null;
  status: string;
  created_at: string;
};

const RESERVATION_STATUSES = [
  "new",
  "contacted",
  "confirmed",
  "seated",
  "completed",
  "cancelled",
];

const ENQUIRY_STATUSES = [
  "new",
  "contacted",
  "quoted",
  "confirmed",
  "completed",
  "closed",
];

const SERVICE_LABELS: Record<
  string,
  string
> = {
  birthday_cake: "Birthday Cake",
  wedding_cake: "Wedding Cake",
  celebration_cake:
    "Celebration / Custom Cake",
  outside_catering: "Outside Catering",
  corporate_lunches:
    "Corporate Office Lunches",
  meeting_snacks:
    "Meeting Snacks / Tea Breaks",
  private_functions:
    "Private Dinner / Function",
  birthday_celebration:
    "Birthday / Celebration",
  wedding_reception:
    "Wedding / Reception",
  corporate_event: "Corporate Event",
  other: "Other Hospitality Enquiry",
};

function CmsOperationsHeader({
  eyebrow,
  title,
  description,
  icon: Icon,
  onRefresh,
  refreshing,
}: {
  eyebrow: string;
  title: string;
  description: string;
  icon:
    | typeof CalendarDays
    | typeof MessageSquareText;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <>
      <header className="border-b border-[#b5bbae] bg-[#314a3b] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 py-5 sm:px-8 lg:px-10">
          <button
            type="button"
            onClick={() => {
              window.location.href =
                "/nova-hq";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em]"
          >
            <ArrowLeft size={14} />
            Nova HQ
          </button>

          <img
            src="/images/nova/logodark.png"
            onError={(event) => {
              event.currentTarget.src =
                "/images/nova/novatransparent.png";
            }}
            alt="Nova Res Culina"
            className="h-14 w-auto object-contain"
          />
        </div>
      </header>

      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div className="max-w-3xl">
          <span className="flex h-10 w-10 items-center justify-center border border-[#9d725c]/35 text-[#9d725c]">
            <Icon
              size={17}
              strokeWidth={1.4}
            />
          </span>

          <p className="mt-7 text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
            {eyebrow}
          </p>

          <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none sm:text-6xl">
            {title}
          </h1>

          <p className="mt-5 max-w-2xl text-sm leading-7 text-[#667067]">
            {description}
          </p>
        </div>

        <button
          type="button"
          disabled={refreshing}
          onClick={onRefresh}
          className="flex h-11 items-center gap-3 border border-[#9d725c] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c] disabled:opacity-50"
        >
          <RefreshCcw size={14} />
          Refresh
        </button>
      </div>
    </>
  );
}

export function NovaCmsReservations() {
  const [rows, setRows] = useState<
    ReservationRow[]
  >([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: loadError } =
      await supabase
        .from("nova_reservations")
        .select(
          "id,guest_name,email,reservation_date,reservation_time,guest_label,note,status,created_at",
        )
        .order("created_at", {
          ascending: false,
        });

    if (loadError) {
      console.error(
        "[Nova CMS] reservation load failed",
        loadError,
      );

      setError(loadError.message);
      setRows([]);
    } else {
      setRows(
        (data ?? []) as ReservationRow[],
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function updateStatus(
    id: string,
    status: string,
  ) {
    const { error: updateError } =
      await supabase
        .from("nova_reservations")
        .update({ status })
        .eq("id", id);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setRows((current) =>
      current.map((row) =>
        row.id === id
          ? {
              ...row,
              status,
            }
          : row,
      ),
    );
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <CmsOperationsHeader
        eyebrow="Guest operations"
        title="Reservations"
        description="Every table request received from the Nova website appears here. Update its status as the team contacts and confirms the guest."
        icon={CalendarDays}
        onRefresh={() => void load()}
        refreshing={loading}
      />

      <section className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-10">
        {error && (
          <div className="mb-6 border border-[#9d725c]/30 bg-[#9d725c]/8 px-5 py-4 text-xs text-[#7d5544]">
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#718076]">
            Loading reservations…
          </p>
        ) : rows.length === 0 ? (
          <div className="border border-[#b5bbae] bg-[#f4efe4] p-10">
            <h2 className="font-['Cormorant_Garamond'] text-4xl">
              No reservations yet.
            </h2>
            <p className="mt-3 text-sm text-[#667067]">
              New website reservations
              will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => (
              <article
                key={row.id}
                className="grid gap-6 border border-[#b5bbae] bg-[#f4efe4] p-6 lg:grid-cols-[1.2fr_.8fr_.8fr_1fr]"
              >
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                    Guest
                  </p>

                  <h2 className="mt-3 font-['Cormorant_Garamond'] text-3xl">
                    {row.guest_name}
                  </h2>

                  <a
                    href={`mailto:${row.email}`}
                    className="mt-2 block text-xs text-[#667067]"
                  >
                    {row.email}
                  </a>

                  {row.note && (
                    <p className="mt-4 max-w-xl text-xs leading-6 text-[#667067]">
                      {row.note}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8d988d]">
                    Table
                  </p>

                  <p className="mt-3 text-sm">
                    {row.reservation_date}
                  </p>

                  <p className="mt-1 text-sm">
                    {row.reservation_time.slice(
                      0,
                      5,
                    )}
                  </p>

                  <p className="mt-1 text-sm">
                    {row.guest_label} guests
                  </p>
                </div>

                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8d988d]">
                    Received
                  </p>

                  <p className="mt-3 text-xs leading-5">
                    {new Date(
                      row.created_at,
                    ).toLocaleString("en-KE")}
                  </p>
                </div>

                <label>
                  <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8d988d]">
                    Status
                  </span>

                  <select
                    value={row.status}
                    onChange={(event) =>
                      void updateStatus(
                        row.id,
                        event.target.value,
                      )
                    }
                    className="mt-3 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 py-3 text-xs outline-none"
                  >
                    {RESERVATION_STATUSES.map(
                      (status) => (
                        <option
                          key={status}
                          value={status}
                        >
                          {status}
                        </option>
                      ),
                    )}
                  </select>
                </label>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

export function NovaCmsEnquiries() {
  const [rows, setRows] = useState<
    EnquiryRow[]
  >([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: loadError } =
      await supabase
        .from("nova_service_enquiries")
        .select(
          "id,guest_name,phone,email,service_type,event_date,guest_count,budget,location,preferred_time,requirements,dietary_requirements,status,created_at",
        )
        .order("created_at", {
          ascending: false,
        });

    if (loadError) {
      console.error(
        "[Nova CMS] enquiry load failed",
        loadError,
      );

      setError(loadError.message);
      setRows([]);
    } else {
      setRows(
        (data ?? []) as EnquiryRow[],
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function updateStatus(
    id: string,
    status: string,
  ) {
    const { error: updateError } =
      await supabase
        .from("nova_service_enquiries")
        .update({ status })
        .eq("id", id);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setRows((current) =>
      current.map((row) =>
        row.id === id
          ? {
              ...row,
              status,
            }
          : row,
      ),
    );
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <CmsOperationsHeader
        eyebrow="Guest operations"
        title="Service enquiries"
        description="Cakes, catering, office lunches, meeting snacks, celebrations and private functions — all guest requests are collected here."
        icon={MessageSquareText}
        onRefresh={() => void load()}
        refreshing={loading}
      />

      <section className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-10">
        {error && (
          <div className="mb-6 border border-[#9d725c]/30 bg-[#9d725c]/8 px-5 py-4 text-xs text-[#7d5544]">
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#718076]">
            Loading enquiries…
          </p>
        ) : rows.length === 0 ? (
          <div className="border border-[#b5bbae] bg-[#f4efe4] p-10">
            <h2 className="font-['Cormorant_Garamond'] text-4xl">
              No enquiries yet.
            </h2>
            <p className="mt-3 text-sm text-[#667067]">
              New service enquiries will
              appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => (
              <article
                key={row.id}
                className="border border-[#b5bbae] bg-[#f4efe4] p-6"
              >
                <div className="grid gap-7 lg:grid-cols-[1.1fr_1fr_1fr_.8fr]">
                  <div>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                      {
                        SERVICE_LABELS[
                          row.service_type
                        ] ||
                        row.service_type
                      }
                    </p>

                    <h2 className="mt-3 font-['Cormorant_Garamond'] text-3xl">
                      {row.guest_name}
                    </h2>

                    <a
                      href={`tel:${row.phone}`}
                      className="mt-2 block text-xs text-[#667067]"
                    >
                      {row.phone}
                    </a>

                    {row.email && (
                      <a
                        href={`mailto:${row.email}`}
                        className="mt-1 block text-xs text-[#667067]"
                      >
                        {row.email}
                      </a>
                    )}
                  </div>

                  <div className="text-xs leading-6">
                    <p>
                      <strong>Date:</strong>{" "}
                      {row.event_date ||
                        "Not supplied"}
                    </p>
                    <p>
                      <strong>People:</strong>{" "}
                      {row.guest_count ??
                        "Not supplied"}
                    </p>
                    <p>
                      <strong>Budget:</strong>{" "}
                      {row.budget ||
                        "Not supplied"}
                    </p>
                    <p>
                      <strong>Time:</strong>{" "}
                      {row.preferred_time ||
                        "Not supplied"}
                    </p>
                  </div>

                  <div className="text-xs leading-6">
                    <p>
                      <strong>Location:</strong>{" "}
                      {row.location ||
                        "Not supplied"}
                    </p>

                    {row.requirements && (
                      <p className="mt-3 text-[#667067]">
                        {row.requirements}
                      </p>
                    )}

                    {row.dietary_requirements && (
                      <p className="mt-3 text-[#667067]">
                        Dietary:{" "}
                        {
                          row.dietary_requirements
                        }
                      </p>
                    )}
                  </div>

                  <label>
                    <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8d988d]">
                      Status
                    </span>

                    <select
                      value={row.status}
                      onChange={(event) =>
                        void updateStatus(
                          row.id,
                          event.target.value,
                        )
                      }
                      className="mt-3 w-full border border-[#b5bbae] bg-[#eee9dc] px-3 py-3 text-xs outline-none"
                    >
                      {ENQUIRY_STATUSES.map(
                        (status) => (
                          <option
                            key={status}
                            value={status}
                          >
                            {status}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
